import { SvgIconComponent } from '@mui/icons-material';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import { toApiError, useGetPolicies } from '@practium/features/api';
import { useEffect, useState } from 'react';
import { Policy, PolicyType } from '../types/policy.types';

type LegalLinksType = {
	key: PolicyType;
	label: string;
	icon: SvgIconComponent;
	policy: Policy | null;
};

export const useLegalPolicies = () => {
	const { data, isLoading: isPoliciesLoading } = useGetPolicies();
	const [termsOfService, setTermsOfService] = useState<Policy | null>(null);
	const [privacyPolicy, setPrivacyPolicy] = useState<Policy | null>(null);
	const [dataProcessing, setDataProcessing] = useState<Policy | null>(null);
	const [thirdPartySharing, setThirdPartySharing] = useState<Policy | null>(null);
	const [marketing, setMarketing] = useState<Policy | null>(null);

	useEffect(() => {
		const loadActivePolicies = async () => {
			try {
				if (data) {
					const activeTerms = data.find((p) => p.policyType === 'terms_of_use' && p.isActive);
					const activePrivacy = data.find((p) => p.policyType === 'privacy_policy' && p.isActive);
					const activeDataProcessing = data.find(
						(p) => p.policyType === 'data_processing' && p.isActive
					);
					const activeThirdPartySharing = data.find(
						(p) => p.policyType === 'third_party_sharing' && p.isActive
					);
					const activeMarketing = data.find((p) => p.policyType === 'marketing' && p.isActive);

					setTermsOfService(activeTerms || null);
					setPrivacyPolicy(activePrivacy || null);
					setDataProcessing(activeDataProcessing || null);
					setThirdPartySharing(activeThirdPartySharing || null);
					setMarketing(activeMarketing || null);
				}
			} catch (error) {
				throw toApiError(error, 'Erro ao carregar os termos.');
			}
		};

		loadActivePolicies();
	}, [data]);

	const getPolicyByType = (type: PolicyType): Policy | null => {
		switch (type) {
			case 'terms_of_use':
				return termsOfService;
			case 'privacy_policy':
				return privacyPolicy;
			case 'data_processing':
				return dataProcessing;
			case 'third_party_sharing':
				return thirdPartySharing;
			case 'marketing':
				return marketing;
			default:
				return null;
		}
	};

	const legalLinks: LegalLinksType[] = [
		{
			key: 'terms_of_use',
			label: 'Termos de Uso',
			policy: termsOfService,
			icon: DescriptionOutlinedIcon,
		},
		{
			key: 'privacy_policy',
			label: 'Política de Privacidade',
			policy: privacyPolicy,
			icon: GavelOutlinedIcon,
		},
		{
			key: 'data_processing',
			label: 'Política de Processamento de Dados',
			policy: dataProcessing,
			icon: DescriptionOutlinedIcon,
		},
		{
			key: 'third_party_sharing',
			label: 'Política de Compartilhamento com Terceiros',
			policy: thirdPartySharing,
			icon: GavelOutlinedIcon,
		},
		{
			key: 'marketing',
			label: 'Política de Marketing',
			policy: marketing,
			icon: GavelOutlinedIcon,
		},
	];

	const activeLegalLinks = legalLinks.filter((link) => link.policy?.isActive);

	return {
		activeLegalLinks,
		termsOfService,
		privacyPolicy,
		isLoading: isPoliciesLoading,
		getPolicyByType,
	};
};
